import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Select from '../Select'
import withData from '../../lib/withData'
import { Input, FormGroup, Label } from 'reactstrap'
import ErrorBoundary from '../ErrorBoundary'
import { SCHOOL_IS_NOT_LISTED_WARNING } from './constants'

const SelectWithData = withData(Select)

const SchoolLookUpLayout = ({
  city,
  country,
  disabled,
  handleChange,
  handleLoadStateData,
  handleSchoolNameChange,
  handleSchoolSelect,
  hasRegionData,
  netsuiteCustomerId,
  nsCustomerId,
  region,
  selectCitiesEndpoint,
  selectSchoolsEndpoint,
  organizationName,
}) => {
  const [isListed, setIsListed] = useState(false)
  const [showRequired, setShowRequired] = useState({
    country: false,
    region: false,
    city: false,
    nsCustomerId: false,
    netsuiteCustomerId: false,
    organizationName: false,
  })

  const [netSuiteCustomersData, setNetSuiteCustomersData] = useState(null)

  useEffect(() => {
    checkIfListed()
    getNetsuiteCustomerId()
  }, [nsCustomerId, netsuiteCustomerId, netSuiteCustomersData, organizationName])

  const checkIfListed = () => {
    if (
      netsuiteCustomerId &&
      netSuiteCustomersData &&
      netSuiteCustomersData.some(
        netSuiteCustomer =>
          netSuiteCustomer.id === netsuiteCustomerId &&
          netSuiteCustomer.id !== 0,
      )
    ) {
      setIsListed(true)
      return
    }

    if (
      nsCustomerId === null ||
      nsCustomerId === undefined ||
      nsCustomerId === '' ||
      netsuiteCustomerId === 0
    ) {
      setIsListed(false)
    }

    if (netSuiteCustomersData == null && !!organizationName) {
      setIsListed(false)
      return
    }

    if (
      netSuiteCustomersData &&
      netSuiteCustomersData.some(
        netSuiteCustomer =>
          netSuiteCustomer.nsCustomerId === nsCustomerId &&
          netSuiteCustomer.id !== 0,
      )
    ) {
      setIsListed(true)
    } else {
      setIsListed(false)
    }
  }

  const getNetSuiteCustomerObject = e => {
    const netSuiteCustomer =
      netSuiteCustomersData &&
      netSuiteCustomersData.find(
        netSuiteCustomer =>
          netSuiteCustomer.id === parseInt(e.target.value, 10) ||
          netSuiteCustomer.id === 0,
      )

    return {
      netsuiteCustomerId: (netSuiteCustomer && netSuiteCustomer.id) || 0,
      nsCustomerId: (netSuiteCustomer && netSuiteCustomer.nsCustomerId) || null,
      companyName: (netSuiteCustomer && netSuiteCustomer.companyName) || null,
    }
  }

  const checkRequired = e => {
    if (e.target.id === 'region') {
      setShowRequired({
        ...showRequired,
        region: hasRegionData && e.target.value === '',
      })
      return
    }
    setShowRequired({ ...showRequired, [e.target.id]: e.target.value === '' })
  }

  const isInNetSuiteCustomersData = () => {
    if (netsuiteCustomerId) {
      return (
        netsuiteCustomerId &&
        netSuiteCustomersData &&
        netSuiteCustomersData.some(nsc => nsc.id === netsuiteCustomerId)
      )
    } else {
      return (
        nsCustomerId &&
        netSuiteCustomersData &&
        netSuiteCustomersData.some(nsc => nsc.nsCustomerId === nsCustomerId)
      )
    }
  }

  const getNetsuiteCustomerId = () =>
    !isInNetSuiteCustomersData() ? 0 : netsuiteCustomerId

  return (
    <ErrorBoundary>
      <SelectWithData
        apiEndpoint="/location/countries"
        disabled={disabled}
        enableShowRequired={true}
        label="Country *"
        name="country"
        onBlur={checkRequired}
        onChange={e => {
          handleChange(e)
          checkRequired(e)
        }}
        showRequired={showRequired.country}
        value={country}
      />
      <SelectWithData
        apiEndpoint={country ? `/location/regions?country=${country}` : ''}
        disabled={disabled || !hasRegionData}
        enableShowRequired={!disabled}
        label="State/Province/Territory *"
        name="region"
        onBlur={checkRequired}
        onChange={e => {
          handleChange(e)
          checkRequired(e)
        }}
        onLoadData={handleLoadStateData}
        showRequired={showRequired.region}
        value={region}
      />
      <SelectWithData
        apiEndpoint={selectCitiesEndpoint()}
        disabled={disabled || (hasRegionData && !region)}
        enableShowRequired={true}
        label="City *"
        name="city"
        onBlur={checkRequired}
        onChange={e => {
          handleChange(e)
          checkRequired(e)
        }}
        showRequired={showRequired.city}
        value={city}
      />
      <SelectWithData
        apiEndpoint={selectSchoolsEndpoint()}
        disabled={disabled}
        enableShowRequired={true}
        label="Assigned Site *"
        name="netSuiteCustomerId"
        onBlur={checkRequired}
        onChange={e => {
          handleSchoolSelect(getNetSuiteCustomerObject(e))
          checkRequired(e)
        }}
        warning={(!isListed && !disabled) ? SCHOOL_IS_NOT_LISTED_WARNING : null} 
        onLoadData={data => {
          setNetSuiteCustomersData(data)
          return data
        }}
        renderOptions={data => {
          return data.map(value => (
            <option key={value.id} value={value.id}>
              {value.companyName}
            </option>
          ))
        }}
        showRequired={showRequired.netsuiteCustomerId}
        value={getNetsuiteCustomerId()}
      />
      {!isListed && (
        <FormGroup>
          <Label for="organizationName">
            Organization *{' '}
            {showRequired.organizationName && (
              <span style={{ color: '#B8002F' }}>Required</span>
            )}
          </Label>
          <Input
            disabled={disabled}
            id="organizationName"
            name="organizationName"
            onBlur={checkRequired}
            onChange={e => {
              handleSchoolNameChange(e)
              checkRequired(e)
            }}
            value={organizationName}
          />
        </FormGroup>
      )}
    </ErrorBoundary>
  )
}

SchoolLookUpLayout.propTypes = {
  city: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  handleLoadStateData: PropTypes.func.isRequired,
  handleChange: PropTypes.func,
  handleSchoolNameChange: PropTypes.func.isRequired,
  handleSchoolSelect: PropTypes.func.isRequired,
  hasRegionData: PropTypes.bool.isRequired,
  nsCustomerId: PropTypes.string,
  region: PropTypes.string,
  selectCitiesEndpoint: PropTypes.func.isRequired,
  selectSchoolsEndpoint: PropTypes.func.isRequired,
  netsuiteCustomerId: PropTypes.number,
  organizationName: PropTypes.string,
}

export default SchoolLookUpLayout



// WEBPACK FOOTER //
// ./src/components/SchoolLookUp/SchoolLookUpLayout.js